import {
  useErrValidationFailedContext,
  useWriteValidationError,
  ErrValidationFailedContextProvider,
} from "@/shared/lib/errors/err-validation-failed-context";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { DocumentState } from "@/services/backend/documents/types/state";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  OfferDocument,
  useVbsOfferPdfDownloadMutation,
  useLazyVbsOfferPdfPreviewQuery,
  useVbsOfferUpdateStateMutation,
} from "@/services/backend/vbs/offers/api/offer-api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  ContractDocument,
  useVbsContractDownloadPDFMutation,
  useLazyVbsContractPreviewPDFQuery,
} from "@/services/backend/vbs/contracts/api/contract-api";
import { Offer } from "@/services/backend/vbs/offers/types/offer";
import { isZeroUUID } from "@/shared/lib/utilities/uuid";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { isImmutable } from "@/services/backend/documents/lib/document-state";

export function OfferPdf({ offer }: { offer: Offer }) {
  const previewRequest = {
    id: offer.id,
    document: OfferDocument.ALL,
  };
  const [loadPreview, { data, isLoading, isFetching, error }] =
    useLazyVbsOfferPdfPreviewQuery();

  const contractPreviewRequest = {
    id: offer.contractId,
    document: ContractDocument.ALL,
  };
  const [
    loadContractPreview,
    {
      data: contractData,
      isLoading: contractIsLoading,
      isFetching: contractIsFetching,
      error: contractError,
    },
  ] = useLazyVbsContractPreviewPDFQuery();

  const { write } = useErrValidationFailedContext();
  const { user } = useAuth();

  // The preview is rendered by the backend. It is loaded when the sheet
  // opens, not on every auto-save. Reopening shows the last one until the
  // fresh one arrives.
  const onOpenChange = (open: boolean) => {
    if (open) {
      loadPreview(previewRequest);
      loadContractPreview(contractPreviewRequest);
    }
  };

  return (
    <DocumentSheet onOpenChange={onOpenChange}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <Tabs className="md:col-span-3" defaultValue="offer">
            <TabsList>
              <TabsTrigger value="offer">{t("Angebot")}</TabsTrigger>
              <TabsTrigger value="contract">{t("Vertrag")}</TabsTrigger>
            </TabsList>
            <TabsContent value="offer">
              <DocumentSheetPreview
                objectURL={data?.objectURL}
                isLoading={isLoading}
                isFetching={isFetching}
                error={error}
              />
            </TabsContent>
            <TabsContent value="contract">
              <RTKQueryErrorAlert error={contractError} />
              {isZeroUUID(offer.contractId) && (
                <div className="text-muted-foreground text-center">
                  {t("Kein Vertrag vorhanden")}
                </div>
              )}
              <DocumentSheetPreview
                objectURL={contractData?.objectURL}
                isLoading={contractIsLoading}
                isFetching={contractIsFetching}
                error={contractError}
              />
            </TabsContent>
          </Tabs>
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Angebot")}
              documentNumber={
                <ReferenceNumber referenceNumber={offer.referenceNumber} />
              }
            />
            <DocumentSheetActionsContent>
              {!isImmutable(offer) && (
                <>
                  <DocumentNotReadyAlert document={t("Angebot")} />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      "Um eine Angebotsnummer zu generieren, schreiben Sie das Angebot fest.",
                    )}
                  />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: offer.id,
                      state: DocumentState.Immutable,
                      stateSetBy: user?.id || "",
                    }}
                    mutation={useVbsOfferUpdateStateMutation}
                    description={t(
                      "Das Angebot wird festgeschrieben und es wird eine Angebotsnummer generiert. Danach kann das Angebot nicht mehr geändert werden.",
                    )}
                    onSuccess={() => {
                      loadPreview(previewRequest);
                      loadContractPreview(contractPreviewRequest);
                    }}
                    onError={(err) => write(err)}
                  />
                  <Separator />
                </>
              )}
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.ALL}
                  text={t("Alle Dokumente")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.OFFER}
                  text={t("Angebot")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.TREATMENT}
                  text={t("Leistungsverzeichnis")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.TREATMENT_BY_AREA}
                  text={t("Bereichsaufteilung")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.ORDER}
                  text={t("Auftrag")}
                />
                <Separator className="my-2" />
                {/*
                  The contract is a document of its own. Its errors must not
                  end up in the scope of the offer, so they are listed here.
                */}
                <ErrValidationFailedContextProvider>
                  <DocumentNotReadyAlert document={t("Vertrag")} />
                  <DownloadContractButton
                    contractId={offer.contractId}
                    document={ContractDocument.ALL}
                    text={t("Vertrag mit LV")}
                  />
                  <DownloadContractButton
                    contractId={offer.contractId}
                    document={ContractDocument.CONTRACT}
                    text={t("Vertrag ohne LV")}
                  />
                  <DownloadContractButton
                    contractId={offer.contractId}
                    document={ContractDocument.TREATMENT}
                    text={t("LV für Vertrag")}
                  />
                </ErrValidationFailedContextProvider>
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  offerId,
  document: documentType,
  text,
}: {
  offerId: string;
  document: OfferDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useVbsOfferPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useWriteValidationError(error, {
    onWrite: () => {
      setOpen(false);
      reset();
    },
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({ id: offerId, document: documentType });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Angebot herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn ein Angebot heruntergeladen wird, wird es festgeschrieben. Es kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} ignoreValidationError={false} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DownloadContractButton({
  contractId,
  document: documentType,
  text,
}: {
  contractId: string;
  document: ContractDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useVbsContractDownloadPDFMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useWriteValidationError(error, {
    onWrite: () => {
      setOpen(false);
      reset();
    },
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({
      id: contractId,
      document: documentType,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild disabled={isZeroUUID(contractId)}>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Vertrag herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn ein Vertrag heruntergeladen wird, wird er festgeschrieben. Er kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
